import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  TextField,
  Tooltip,
  IconButton
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import HotelIcon from "@mui/icons-material/Hotel";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { extractHotelFromPdf } from "../../api/hotelsAPI";

const QUICK_PROMPT_SUGGESTIONS = [
  { label: "📍 Set Destination", text: "Destination is: " },
  { label: "📅 Season: Apr-Jun", text: "Season is 01-Apr to 30-Jun, Off-season is 01-Jul to 30-Sep. " },
  { label: "➕ Add 12% GST", text: "Please add 12% GST to all room rates. " },
  { label: "➕ Add 18% GST", text: "Please add 18% GST to all room rates. " },
  { label: "🏨 Deluxe & Suite only", text: "Only extract Deluxe and Suite room categories. " },
  { label: "⭐ 4-Star Luxury", text: "Hotel type is Luxury, rating is 4. " }
];

const HotelPdfUpload = ({ onExtracted, mode = "add", onBatchSave }) => {
  const [loading, setLoading] = useState(false);
  const [batchSaving, setBatchSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [promptText, setPromptText] = useState("");
  const [extractedHotels, setExtractedHotels] = useState([]);
  const [activeHotelIndex, setActiveHotelIndex] = useState(0);

  const fileInputRef = useRef(null);

  const handleFilesAdded = (newFilesList) => {
    if (!newFilesList || newFilesList.length === 0) return;

    const validPdfs = Array.from(newFilesList).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    );

    if (validPdfs.length === 0) {
      setErrorMsg("Please upload valid PDF file(s) (.pdf).");
      return;
    }

    setErrorMsg(null);
    setStatusMsg(null);

    // Merge without duplicates by name
    setSelectedFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const combined = [...prev];
      for (const f of validPdfs) {
        if (!existingNames.has(f.name)) {
          combined.push(f);
          existingNames.add(f.name);
        }
      }
      return combined;
    });
  };

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleClearAllFiles = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const executeExtraction = async () => {
    if (selectedFiles.length === 0 && !promptText.trim()) {
      setErrorMsg("Please select at least one PDF file or enter prompt instructions.");
      return;
    }

    const totalSize = selectedFiles.reduce((acc, f) => acc + f.size, 0);
    if (totalSize > 50 * 1024 * 1024) {
      setErrorMsg("Total file size exceeds 50 MB limit. Please remove some PDFs.");
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    setStatusMsg(null);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append("files", file);
      });

      if (promptText.trim()) {
        formData.append("prompt", promptText.trim());
        formData.append("customPrompt", promptText.trim());
      }

      const response = await extractHotelFromPdf(formData);

      let hotelsList = [];
      if (Array.isArray(response?.hotels) && response.hotels.length > 0) {
        hotelsList = response.hotels;
      } else if (response?.data) {
        hotelsList = [response.data];
      }

      if (hotelsList.length > 0) {
        setExtractedHotels(hotelsList);
        setActiveHotelIndex(0);

        const sourceLabel =
          selectedFiles.length > 0
            ? `${selectedFiles.length} PDF${selectedFiles.length > 1 ? "s" : ""}`
            : "prompt instructions";

        if (hotelsList.length === 1) {
          const h = hotelsList[0];
          setStatusMsg(
            `Extracted "${h.hotel_name || "Hotel"}" with ${
              h.category?.length || 0
            } room categories from ${sourceLabel}${promptText.trim() && selectedFiles.length > 0 ? " + custom prompt" : ""}!`
          );
        } else {
          setStatusMsg(
            `Found ${hotelsList.length} hotels from ${sourceLabel}! "${
              hotelsList[0].hotel_name || "Hotel 1"
            }" is currently loaded in the form.`
          );
        }

        if (typeof onExtracted === "function") {
          onExtracted(hotelsList[0], hotelsList);
        }
      } else {
        setErrorMsg(response?.message || "Could not extract hotel details from the provided input.");
      }
    } catch (err) {
      console.error("PDF/Prompt extraction error:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to process with AI. Please check the file(s) or prompt and try again.";
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFilesAdded(files);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFilesAdded(files);
    }
  };

  const handleSelectHotel = (idx) => {
    setActiveHotelIndex(idx);
    const hotel = extractedHotels[idx];
    if (hotel && typeof onExtracted === "function") {
      onExtracted(hotel, extractedHotels);
    }
  };

  const handleSaveAll = async () => {
    if (!onBatchSave || extractedHotels.length === 0) return;
    setBatchSaving(true);
    try {
      const sanitized = extractedHotels.map((h) => ({
        ...h,
        type: (h.type && h.type !== "Hotel") ? h.type : "Deluxe"
      }));
      await onBatchSave(sanitized);
    } finally {
      setBatchSaving(false);
    }
  };

  const handleQuickChipClick = (suggestion) => {
    setPromptText((prev) => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed}\n${suggestion.text}` : suggestion.text;
    });
  };

  const hasInput = selectedFiles.length > 0 || promptText.trim().length > 0;

  return (
    <Paper
      elevation={0}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      sx={{
        p: 2.5,
        mb: 2.5,
        borderRadius: 2,
        background: "linear-gradient(135deg, #f0f7ff 0%, #f5f3ff 100%)",
        border: dragActive ? "2px dashed #6366f1" : "1.5px dashed #93c5fd",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "#6366f1",
          background: "linear-gradient(135deg, #eff6ff 0%, #f3e8ff 100%)"
        }
      }}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="application/pdf,.pdf"
        multiple
        style={{ display: "none" }}
      />

      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: "#4f46e5",
              color: "#fff",
              boxShadow: "0 2px 6px rgba(79, 70, 229, 0.3)"
            }}
          >
            <AutoAwesomeIcon fontSize="small" />
          </Box>
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight={700} color="#1e1b4b">
              AI Auto-Fill with PDF & Custom Prompt
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upload one or multiple PDF rate sheets, OR enter custom instructions/tariff text. Gemini AI will auto-populate all hotel details, meal plans & room rates.
            </Typography>
          </Box>
        </Stack>

        {/* Upload & Action Row */}
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            disabled={loading}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              bgcolor: "#ffffff",
              borderColor: "#c7d2fe",
              color: "#4338ca",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#f5f3ff", borderColor: "#818cf8" }
            }}
          >
            {selectedFiles.length > 0 ? "+ Add More PDFs" : "Choose Hotel PDF(s)"}
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
            disabled={loading || !hasInput}
            onClick={executeExtraction}
            sx={{
              bgcolor: "#4f46e5",
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "0 2px 8px rgba(79, 70, 229, 0.25)",
              "&:hover": { bgcolor: "#4338ca" }
            }}
          >
            {loading
              ? "Extracting with Gemini AI..."
              : selectedFiles.length > 0
                ? `⚡ Extract & Auto-Fill (${selectedFiles.length} PDF${selectedFiles.length > 1 ? "s" : ""}${promptText.trim() ? " + Prompt" : ""})`
                : "⚡ Extract & Auto-Fill from Prompt"}
          </Button>

          <Typography variant="caption" color="text.secondary">
            or drag & drop PDFs (multiple allowed)
          </Typography>
        </Box>

        {/* Selected PDF Chips */}
        {selectedFiles.length > 0 && (
          <Box sx={{ p: 1.2, bgcolor: "#ffffff", borderRadius: 1.5, border: "1px solid #e0e7ff" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="caption" fontWeight={600} color="#4338ca">
                📄 Selected Files ({selectedFiles.length}):
              </Typography>
              <Button
                size="small"
                color="inherit"
                onClick={handleClearAllFiles}
                disabled={loading}
                sx={{ textTransform: "none", fontSize: "0.72rem", py: 0, minHeight: 0, color: "text.secondary" }}
              >
                Clear all
              </Button>
            </Stack>
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
              {selectedFiles.map((file, i) => (
                <Chip
                  key={i}
                  size="small"
                  icon={<PictureAsPdfIcon fontSize="small" />}
                  label={`${file.name} (${(file.size / 1024).toFixed(0)} KB)`}
                  onDelete={loading ? undefined : () => handleRemoveFile(i)}
                  variant="outlined"
                  sx={{
                    fontSize: "0.75rem",
                    bgcolor: "#f8fafc",
                    borderColor: "#c7d2fe",
                    color: "#334155"
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Custom Prompt / Instructions Section */}
        <Box sx={{ p: 1.5, bgcolor: "#ffffff", borderRadius: 1.5, border: "1px solid #e0e7ff" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.8}>
            <Stack direction="row" alignItems="center" spacing={0.8}>
              <TuneIcon sx={{ fontSize: 18, color: "#4f46e5" }} />
              <Typography variant="subtitle2" fontWeight={700} color="#1e1b4b">
                Custom Instructions / Prompt (Optional)
              </Typography>
            </Stack>

            {promptText && (
              <Button
                size="small"
                color="inherit"
                onClick={() => setPromptText("")}
                disabled={loading}
                startIcon={<ClearIcon fontSize="inherit" />}
                sx={{ textTransform: "none", fontSize: "0.72rem", py: 0, color: "text.secondary" }}
              >
                Clear
              </Button>
            )}
          </Stack>

          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            size="small"
            disabled={loading}
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            placeholder="Give custom instructions or paste tariff text, e.g.:&#10;• 'Destination is Pelling, West Sikkim, exclude Deluxe rooms'&#10;• 'Add 12% GST to all prices' or 'Season is 01-Apr to 30-Jun'&#10;• Or paste hotel WhatsApp/email tariff details directly here..."
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.85rem",
                borderRadius: 1.5,
                bgcolor: "#fafafa",
                "& fieldset": { borderColor: "#c7d2fe" },
                "&:hover fieldset": { borderColor: "#818cf8" },
                "&.Mui-focused fieldset": { borderColor: "#4f46e5" }
              }
            }}
          />

          {/* Quick suggestion helper chips */}
          <Stack direction="row" spacing={0.6} mt={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ display: "inline-flex", alignItems: "center", mr: 0.5 }}>
              <LightbulbOutlinedIcon sx={{ fontSize: 14, mr: 0.3, color: "#f59e0b" }} /> Suggestions:
            </Typography>
            {QUICK_PROMPT_SUGGESTIONS.map((s, idx) => (
              <Chip
                key={idx}
                size="small"
                label={s.label}
                onClick={() => handleQuickChipClick(s)}
                disabled={loading}
                sx={{
                  fontSize: "0.72rem",
                  height: 24,
                  bgcolor: "#f1f5f9",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#e2e8f0" }
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Loading state banner */}
        {loading && (
          <Box sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.9)", borderRadius: 1.5, border: "1px solid #c7d2fe" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={22} sx={{ color: "#4f46e5" }} />
              <Box>
                <Typography variant="body2" fontWeight={600} color="#4338ca">
                  Analyzing with Gemini AI...
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Parsing hotel details, room categories, plan tariffs (EP/CP/MAP/AP), extra mattress rates & dates
                  {promptText.trim() ? " incorporating your custom prompt" : ""}.
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Success message */}
        {statusMsg && !loading && (
          <Alert
            severity="success"
            icon={<CheckCircleOutlineIcon fontSize="inherit" />}
            onClose={() => setStatusMsg(null)}
            sx={{ borderRadius: 1.5 }}
          >
            <strong>✨ Successfully auto-filled!</strong> {statusMsg} Review fields below before saving.
          </Alert>
        )}

        {/* Multiple Hotels Extracted Banner */}
        {extractedHotels.length > 1 && !loading && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: "#ffffff",
              borderRadius: 1.5,
              border: "1px solid #c7d2fe",
              boxShadow: "0 1px 4px rgba(99, 102, 241, 0.08)"
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="#3730a3">
                  🏢 {extractedHotels.length} Hotels Extracted
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click any hotel to inspect and load it into the form:
                </Typography>
              </Box>

              {mode === "add" && typeof onBatchSave === "function" && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={batchSaving ? <CircularProgress size={16} color="inherit" /> : <DoneAllIcon />}
                  onClick={handleSaveAll}
                  disabled={batchSaving}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "0 2px 6px rgba(16, 185, 129, 0.3)"
                  }}
                >
                  {batchSaving ? "Saving All..." : `Save All (${extractedHotels.length}) Hotels`}
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {extractedHotels.map((h, idx) => {
                const isActive = activeHotelIndex === idx;
                return (
                  <Chip
                    key={idx}
                    icon={<HotelIcon fontSize="small" />}
                    label={`${idx + 1}. ${h.hotel_name || "Unnamed"} (${h.destination || "N/A"}) - ${h.category?.length || 0} rooms`}
                    color={isActive ? "primary" : "default"}
                    variant={isActive ? "filled" : "outlined"}
                    onClick={() => handleSelectHotel(idx)}
                    sx={{
                      fontWeight: isActive ? 700 : 500,
                      cursor: "pointer",
                      borderColor: isActive ? "#4f46e5" : "#cbd5e1",
                      bgcolor: isActive ? "#4f46e5" : "#f8fafc",
                      "&:hover": {
                        bgcolor: isActive ? "#4338ca" : "#e2e8f0"
                      }
                    }}
                  />
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Error message */}
        {errorMsg && !loading && (
          <Alert
            severity="error"
            onClose={() => setErrorMsg(null)}
            sx={{ borderRadius: 1.5 }}
          >
            {errorMsg}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

export default HotelPdfUpload;
